import { z } from "zod";
import {
  PersonalAgentDeviceIdSchema,
  PrivateAgentAgentIdSchema,
  PrivateAgentPlacementSchema,
  PrivateAgentPlacementRevisionSchema,
  PrivateAgentProfileProductProjectionSchema,
  PrivateAgentProfileRevisionSchema,
  PrivateAgentProfileSchema,
  SaleskoOpaqueSha256Schema,
  TenantIdSchema
} from "./index";

export const PrivateAgentProfileProjectionPhase3SchemaVersion =
  "salesko.private_agent_profile_projection_state.v1" as const;
export const PrivateAgentProfileProjectionRequestSchemaVersion =
  "salesko.private_agent_profile_projection_request.v1" as const;
export const PrivateAgentProfileReconciliationAudience =
  "salesko.private-agent-profile-reconcile.v1" as const;

const TenantRefSchema = z.string().regex(/^[0-9a-f]{32}$/u);
const BoundedReasonCodeSchema = z.string().min(1).max(96).regex(/^[a-z0-9]+(?:[._-][a-z0-9]+)*$/u);

export const PrivateAgentProfileProjectionRequestSchema = z
  .object({
    schemaVersion: z.literal(PrivateAgentProfileProjectionRequestSchemaVersion),
    requestId: z.uuid(),
    tenantRef: TenantRefSchema,
    deviceId: PersonalAgentDeviceIdSchema,
    placementRevision: PrivateAgentPlacementRevisionSchema,
    projection: PrivateAgentProfileProductProjectionSchema,
    projectionHash: SaleskoOpaqueSha256Schema
  })
  .strict()
  .refine((value) => value.projection.profileRevision.length > 0, {
    path: ["projection", "profileRevision"],
    message: "projection revision is required"
  });

export const PrivateAgentProfileProjectionSyncStatusSchema = z.enum(["pending", "synced", "failed"]);
export const PrivateAgentProfileProjectionSyncStatePhase3Schema = z
  .object({
    schemaVersion: z.literal(PrivateAgentProfileProjectionPhase3SchemaVersion),
    agentId: PrivateAgentAgentIdSchema,
    placementRevision: PrivateAgentPlacementRevisionSchema,
    status: PrivateAgentProfileProjectionSyncStatusSchema,
    deviceId: PersonalAgentDeviceIdSchema,
    profileRevision: PrivateAgentProfileRevisionSchema,
    projectionHash: SaleskoOpaqueSha256Schema,
    requestId: z.uuid(),
    lastAttemptAt: z.string().datetime({ offset: true }).optional(),
    reasonCode: BoundedReasonCodeSchema.optional(),
    appliedRevision: PrivateAgentProfileRevisionSchema.optional(),
    appliedProjectionHash: SaleskoOpaqueSha256Schema.optional()
  })
  .strict()
  .superRefine((value, context) => {
    const hasAppliedRevision = value.appliedRevision !== undefined;
    const hasAppliedHash = value.appliedProjectionHash !== undefined;
    if (hasAppliedRevision !== hasAppliedHash) {
      context.addIssue({ code: "custom", path: ["appliedRevision"], message: "applied revision and hash must be present together" });
    }
    if (value.status === "synced") {
      if (value.appliedRevision !== value.profileRevision || value.appliedProjectionHash !== value.projectionHash) {
        context.addIssue({ code: "custom", path: ["status"], message: "synced requires the exact desired revision and hash" });
      }
      if (value.reasonCode !== undefined) {
        context.addIssue({ code: "custom", path: ["reasonCode"], message: "synced state cannot carry a failure reason" });
      }
    }
  });

const PrivateAgentProjectionStateSchema = z
  .object({
    profile: PrivateAgentProfileSchema,
    placement: PrivateAgentPlacementSchema,
    projectionSync: PrivateAgentProfileProjectionSyncStatePhase3Schema
  })
  .strict()
  .refine(({ profile, placement, projectionSync }) =>
    profile.agentId === placement.agentId && profile.agentId === projectionSync.agentId
    && profile.profileRevision === projectionSync.profileRevision
    && placement.deviceId === projectionSync.deviceId
    && placement.placementRevision === projectionSync.placementRevision,
  { message: "Profile, Placement and projection state must identify the same exact binding" });

export const PrivateAgentProfileResponsePhase3Schema = PrivateAgentProjectionStateSchema.safeExtend({
  tenantId: TenantIdSchema
});

export const PrivateAgentProfileReconciliationTriggerSchema = z
  .object({
    assertion: z.unknown(),
    agentId: PrivateAgentAgentIdSchema
  })
  .strict();

export const PrivateAgentProfileReconciliationPrincipalSchema = z
  .object({
    tenantRef: TenantRefSchema,
    deviceId: PersonalAgentDeviceIdSchema,
    eventId: z.uuid(),
    agentId: PrivateAgentAgentIdSchema
  })
  .strict();

export type PrivateAgentProfileProjectionRequest = z.infer<typeof PrivateAgentProfileProjectionRequestSchema>;
export type PrivateAgentProfileProjectionSyncStatePhase3 = z.infer<typeof PrivateAgentProfileProjectionSyncStatePhase3Schema>;
export type PrivateAgentProfileResponsePhase3 = z.infer<typeof PrivateAgentProfileResponsePhase3Schema>;
export type PrivateAgentProfileReconciliationTrigger = z.infer<typeof PrivateAgentProfileReconciliationTriggerSchema>;
export type PrivateAgentProfileReconciliationPrincipal = z.infer<typeof PrivateAgentProfileReconciliationPrincipalSchema>;

export const PrivateAgentListResponseSchema = z.object({
  tenantId: TenantIdSchema,
  agents: z.array(PrivateAgentProjectionStateSchema).max(100)
}).strict();
export type PrivateAgentListResponse = z.infer<typeof PrivateAgentListResponseSchema>;

/** Authenticated device collection discovery; never a default Agent selection. */
export const PrivateAgentPlacementInventoryAudience = "salesko.private-agent-placement-inventory.v1" as const;
export const PrivateAgentPlacementInventoryTriggerSchema = z.object({ assertion: z.unknown() }).strict();
export const PrivateAgentPlacementInventoryPrincipalSchema = z.object({
  tenantRef: TenantRefSchema,
  deviceId: PersonalAgentDeviceIdSchema
}).strict();
export const PrivateAgentPlacementInventoryResponseSchema = z.object({
  agents: z.array(z.object({
    agentId: PrivateAgentAgentIdSchema,
    placementRevision: PrivateAgentPlacementRevisionSchema
  }).strict()).max(100)
}).strict();
export type PrivateAgentPlacementInventoryResponse = z.infer<typeof PrivateAgentPlacementInventoryResponseSchema>;
